import { type RefObject, useCallback, useRef } from 'react';
import isEqual from 'react-fast-compare';

import {
  type Box,
  boxesIntersect,
  type SelectionBox,
  type SelectionContainerProps,
  useSelectionContainer,
} from '@air/react-drag-to-select';

export type MarqueeSelectableItem = { id: string; box: Box };

export type UseMarqueeSelectionParams = {
  containerRef: RefObject<HTMLElement>;
  getItems: () => MarqueeSelectableItem[];
  onHighlightChange: (ids: string[]) => void;
  isEnabled: boolean;
  eventsElement: HTMLElement | null;
  shouldStartSelecting?: (target: EventTarget | null) => boolean;
  onSelectionStart?: () => void;
  onSelectionEnd?: () => void;
  minSelectionTopPosition?: number;
  selectionProps?: SelectionContainerProps;
};

export const MARQUEE_SELECTION_PROPS: SelectionContainerProps = {
  style: {
    border: `none`,
    backgroundColor: 'var(--colors-indigo4)',
    opacity: 0.5,
    borderRadius: 4,
    zIndex: 99,
  },
};

export const useMarqueeSelection = ({
  containerRef,
  getItems,
  onHighlightChange,
  isEnabled,
  eventsElement,
  shouldStartSelecting,
  onSelectionStart,
  onSelectionEnd,
  minSelectionTopPosition = 0,
  selectionProps,
}: UseMarqueeSelectionParams): ReturnType<typeof useSelectionContainer> => {
  const lastIdsRef = useRef<string[]>([]);

  const onSelectionChange = useCallback(
    (selectionBox: SelectionBox) => {
      if (containerRef?.current) {
        const selectedIds: string[] = [];

        // prevent selecting items when they are scrolled, but selection is on search or header
        if (selectionBox.top + selectionBox.height >= minSelectionTopPosition) {
          const clientRect = containerRef.current.getBoundingClientRect();
          const normalizedBox = {
            ...selectionBox,
            width: selectionBox.width,
            height: selectionBox.height,
            top: selectionBox.top - clientRect.top,
            left: selectionBox.left - clientRect.left,
          };
          for (const item of getItems()) {
            if (boxesIntersect(item.box, normalizedBox)) {
              selectedIds.push(item.id);
            }
          }
        }

        if (!isEqual(lastIdsRef.current, selectedIds)) {
          lastIdsRef.current = selectedIds;
          onHighlightChange(selectedIds);
        }
      }
    },
    [containerRef, minSelectionTopPosition, getItems, onHighlightChange],
  );

  const handleSelectionStart = useCallback(() => {
    lastIdsRef.current = [];
    onSelectionStart?.();
  }, [onSelectionStart]);

  return useSelectionContainer({
    onSelectionStart: handleSelectionStart,
    onSelectionEnd,
    onSelectionChange,
    isEnabled,
    selectionProps: selectionProps ?? MARQUEE_SELECTION_PROPS,
    eventsElement,
    shouldStartSelecting,
  });
};
